/**
 * API endpoint constants for the application
 * Uses path parameters with colon prefix (e.g., :id) for use with urlcat
 */

// Auth related endpoints
const AUTH = {
  LOGIN: '/auth/signin',
  SIGN_UP: '/auth/signup',
  CHECK_ACCOUNT: '/auth/check-account',
  VERIFY_SIGN_UP_OTP: '/auth/verify-otp-signup',  // Fixed typo in "VERIFY"
  SEND_SIGN_UP_OTP: '/auth/send-otp-signup',
  LOGOUT: '/auth/logout',
  REFRESH_TOKEN: '/auth/refresh-token',
  SEND_OTP_RESET_PASSWORD: '/auth/send-otp-reset-password',
  VERIFY_OTP_RESET_PASSWORD: '/auth/verify-otp-reset-password',
  RESET_PASSWORD: '/auth/reset-password',
};

// User related endpoints
const USER = {
  PROFILE: '/user',
  DELETE_ACCOUNT: '/user',
  UPDATE_PROFILE: '/user/profile',
  UPDATE_AVATAR: '/user/avatar',
  UPDATE_NICKNAME: '/user/nickname',
  CHANGE_PASSWORD: '/user/change-password',
};

// Overview related endpoints
const DASHBOARD = {
  ACCOUNT_BALANCE: '/dashboard/account-balance',
  STATISTICS: '/dashboard/statistics',
  TRANSACTION_SUMMARIZE: '/dashboard/transaction-summarize',
  TRANSACTIONS: '/dashboard/transactions',
};

// Wallet related endpoints
const WALLETS = {
  BASE: '/wallets',
  TOTAL_ASSETS: '/wallets/total-assets',
  BALANCE_FLUCTUATIONS: '/wallets/balance-fluctuations',
  BY_ID: '/wallets/:type/:id',
  EDIT: '/wallets/:type/:id',
  ARCHIVED: '/wallets/:type/:id/archive',
  ADJUST_BALANCE: '/wallets/:type/:id/adjust-balance',
  CREATE_CREDIT: '/wallets/credit',
  CREATE_BASIC: '/wallets/basic',
  DETAIL: '/wallets/:type/:id',
  STATEMENTS: '/wallets/:type/statements/:id',
  STATEMENT_DETAIL: '/wallets/:type/statements/:id/:statementId/credit',
  DEBIT_STATEMENT_DETAIL: '/wallets/:type/statements/:id/:statementId/debit',
  INSTALLMENT: '/wallets/credit/:id/installments',
  UPDATE_INSTALLMENT: '/wallets/credit/:id/installments/:installmentId',
  // LOANS: '/wallets/loan/:id/:loanStatus',
  LOANS: '/wallets/loan/:id/type/:loanStatus',
  UPDATE_ORDER: '/wallets/sort',
};

// Loan related endpoints
const LOANS = {
  BASE: '/wallets/loan',
  BORROWING: '/wallets/loan/:id/borrowing/:loanStatus',
  REPAY: '/wallets/loan/repay',
  LENDING: '/wallets/loan/:id/lending/:loanStatus',
  CLEAR_DEBT: '/wallets/loan/:id/clear-debt',
  OFFSET: '/wallets/loan/:id/offset',
};

// Transaction related endpoints
const TRANSACTIONS = {
  BASE: '/transactions',
  CREATE: '/transactions',           // Unified create endpoint
  UPDATE_BY_ID: '/transactions/:id', // Unified update endpoint
  EXPENSE: '/transactions/expense',  // Deprecated: use CREATE
  INCOME: '/transactions/income',    // Deprecated: use CREATE
  TRANSFER: '/transactions/transfer', // Deprecated: use CREATE
  BY_ID: '/transactions/:id',
  LOAN_BY_ID: '/transactions/:id/loan',
  BORROWING_BY_ID: '/transactions/:transactionId/borrowing',
  BORROW_EXPENSE_BY_ID: '/transactions/:transactionId/borrow-expense',
  REPAYMENT_BY_ID: '/transactions/:transactionId/repayment',
  CLEAR_DEBT_BY_ID: '/transactions/:transactionId/clear-debt',
  LENDING_BY_ID: '/transactions/:transactionId/lending',
  COLLECTING_BY_ID: '/transactions/:transactionId/collecting',
  LOAN_DETAIL_BY_ID: '/wallets/loan/transactions/loan/:transactionId',
  UPDATE_INCOME_TRANSACTION: 'transactions/income/:id',
  UPDATE_EXPENSE_TRANSACTION: 'transactions/expense/:id',
  UPDATE_TRANSFER_TRANSACTION: 'transactions/transfer/:id',
  CREATE_BORROWING: '/wallets/loan/borrowing',
  CREATE_REPAY: '/wallets/loan/repay',
  CREATE_LENDING: '/wallets/loan/lending',
  CREATE_COLLECT: '/wallets/loan/collect',
  UPDATE_REPAY: '/wallets/loan/repay/:transactionId',
  UPDATE_LENDING: '/wallets/loan/lending/:id',
  UPDATE_COLLECT: '/wallets/loan/collect/:id',
  UPDATE_TRANSACTION: '/transactions/:transactionId',
};

// Category related endpoints
const CATEGORIES = {
  ALL: '/categories',
  EXPENSE: '/categories/expense',
  INCOME: '/categories/income',
  EXPENSE_FREQUENTLY_USED: '/categories/expense/frequently-used',
  INCOME_FREQUENTLY_USED: '/categories/income/frequently-used',
  CREATE: '/categories',
  UPDATE: '/categories/:id',
  CREATE_CATEGORY_GROUP: '/categories/groups',
  UPDATE_CATEGORY_GROUP: '/categories/groups/:id',
};

// Budget related endpoints
const BUDGETS = {
  BY_MONTH: '/budgets/:year/:month',
  UPDATE_GROUP: '/budgets/:year/:month/groups/:groupId',
  BATCH_UPDATE_GROUPS: '/budgets/:year/:month/groups/batch',
  UPDATE_DEBT: '/budgets/:year/:month/debts/:creditWalletId',
  UPDATE_SAVINGS: '/budgets/:year/:month/savings/:goalId',
  AUTO_APPLY_SAVINGS: '/budgets/:year/:month/savings/auto-apply',
  HISTORY: '/budgets/history',
  GROUP_SUMMARY: '/budgets/:year/:month/group-summary',
};

// Savings Goals endpoints
const SAVINGS_GOALS = {
  BASE: '/savings-goals',
  BY_ID: '/savings-goals/:id',
};

/**
 * Flat API endpoints object for compatibility with existing code
 * This approach makes it easier for developers to use nested structure
 * while maintaining backward compatibility.
 */
export const API_ENDPOINTS = {
  // Auth
  ...AUTH,

  // User
  ME: USER.PROFILE,

  // Overview
  ACCOUNT_BALANCE: DASHBOARD.ACCOUNT_BALANCE,
  STATISTICS: DASHBOARD.STATISTICS,

  // Wallets
  WALLETS: WALLETS.BASE,
  TOTAL_ASSETS: WALLETS.TOTAL_ASSETS,
  WALLET_BY_ID: WALLETS.BY_ID,
  CREATE_CREDIT_WALLET: WALLETS.CREATE_CREDIT,
  CREATE_BASIC_WALLET: WALLETS.CREATE_BASIC,
  WALLET_DETAIL: WALLETS.DETAIL,
  CREDIT_WALLET_STATEMENTS: WALLETS.STATEMENTS,
  CREDIT_STATEMENT_DETAIL: WALLETS.STATEMENT_DETAIL,

  // Loan Wallets
  LOANS: LOANS.BASE,

  // Transactions
  TRANSACTIONS: TRANSACTIONS.BASE,
  CREATE_EXPENSE_TRANSACTION: TRANSACTIONS.EXPENSE,
  CREATE_INCOME_TRANSACTION: TRANSACTIONS.INCOME,
  CREATE_TRANSFER_TRANSACTION: TRANSACTIONS.TRANSFER,
  TRANSACTION_BY_ID: TRANSACTIONS.BY_ID,

  // Categories
  ALL_CATEGORIES: CATEGORIES.ALL,
  EXPENSE_CATEGORY: CATEGORIES.EXPENSE,
  INCOME_CATEGORY: CATEGORIES.INCOME,
  EXPENSE_FREQUENTLY_USED: CATEGORIES.EXPENSE_FREQUENTLY_USED,
  INCOME_FREQUENTLY_USED: CATEGORIES.INCOME_FREQUENTLY_USED,
  CREATE_CATEGORY: CATEGORIES.CREATE,
  UPDATE_CATEGORY: CATEGORIES.UPDATE,
  CREATE_CATEGORY_GROUP: CATEGORIES.CREATE_CATEGORY_GROUP,
  UPDATE_CATEGORY_GROUP: CATEGORIES.UPDATE_CATEGORY_GROUP,
};

/**
 * Structured API endpoints with categorized groups
 * This approach allows for better organization and discoverability
 */
export const API = {
  AUTH,
  USER,
  DASHBOARD,
  WALLETS,
  TRANSACTIONS,
  CATEGORIES,
  LOANS,
  BUDGETS,
  SAVINGS_GOALS,
};

export default API_ENDPOINTS;
